import {Component, forwardRef, signal, WritableSignal} from '@angular/core';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {IconCalendarComponent} from '../icons/icon-calendar/icon-calendar.component';
import {DatePipe, NgClass} from '@angular/common';
import {IconArrowDownComponent} from '../icons/icon-arrow-down/icon-arrow-down.component';
import {IconArrowLeftComponent} from '../icons/icon-arrow-left/icon-arrow-left.component';
import {IconArrowRightComponent} from '../icons/icon-arrow-right/icon-arrow-right.component';
import {addDays, format, startOfMonth, startOfWeek, subMonths} from 'date-fns';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

@Component({
  selector: 'braidsbook-date-picker',
  imports: [
    CdkOverlayOrigin,
    IconCalendarComponent,
    DatePipe,
    CdkConnectedOverlay,
    IconArrowDownComponent,
    IconArrowLeftComponent,
    IconArrowRightComponent,
    NgClass
  ],
  templateUrl: './date-picker.component.html',
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatePickerComponent), multi: true}
  ]
})
export class DatePickerComponent implements ControlValueAccessor {
  isCalendarOpen: WritableSignal<boolean> = signal(false);

  viewMode: WritableSignal<'years' | 'months'> = signal('months');
  currentDate: WritableSignal<Date> = signal(new Date());
  today: Date = new Date();
  daysOfWeek: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  calendarDays: {date: Date; outside: boolean}[][] = [];
  selectedDate: WritableSignal<Date | null> = signal(null);
  selectedYear: number = this.currentDate().getFullYear();
  protected yearsRange: number[] = [];

  constructor() {
    this.generateCalendar();
    this.generateYearsRange();
  }

  generateCalendar(): void {
    const start = startOfWeek(startOfMonth(this.currentDate()), {weekStartsOn: 1});
    const days: {date: Date, outside: boolean}[] = [];

    let date = start;
    while (days.length < 42) {  // Toujours générer 6 semaines (6 * 7 = 42 jours)
      days.push({date, outside: date.getMonth() !== this.currentDate().getMonth()});
      date = addDays(date, 1);
    }

    this.calendarDays = [];
    while (days.length) {
      this.calendarDays.push(days.splice(0, 7));
    }
  }

  selectDate(date: Date): void {
    this.selectedDate.set(date);
    this.isCalendarOpen.set(false);
    this.onChange(format(date, 'yyyy-MM-dd'));
    this.onTouched();
  }

  toggleViewMode(): void {
    this.viewMode.set(this.viewMode() === 'months' ? 'years' : 'months');
  }

  generateYearsRange(): void {
    const startYear = this.selectedYear - 8;
    this.yearsRange = Array.from({length: 16}, (_, i) => startYear + i);
  }

  selectYear(year: number): void {
    this.selectedYear = year;
    this.currentDate.set(new Date(year, this.currentDate().getMonth(), this.currentDate().getDate()));
    this.selectedDate.set(this.currentDate());
    this.viewMode.set('months');
    this.generateYearsRange();
    this.generateCalendar();
  }

  prev(): void {
    if (this.viewMode() === 'months') {
      this.currentDate.set(subMonths(this.currentDate(), 1));
    } else {
      this.selectedYear -= 16;
    }
    this.generateCalendar();
    this.generateYearsRange();

  }

  next(): void {
    if (this.viewMode() === 'months') {
      this.currentDate.set(subMonths(this.currentDate(), -1));
    } else {
      this.selectedYear += 16;
    }
    this.generateYearsRange();
    this.generateCalendar();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    this.selectedDate.set(obj);
    this.currentDate.set(obj ? new Date(obj) : new Date());
    this.selectedYear = this.currentDate().getFullYear();
    this.generateCalendar();
    this.generateYearsRange();
  }

  private onChange: (value: any) => void = () => {
  };
  private onTouched: () => void = () => {
  };

}
